import React, { FC, Suspense, useCallback, useMemo, useRef, useState } from 'react';
import { graphql } from 'react-relay';
import { useLazyLoadQuery, useMutation } from 'react-relay/hooks';
import AreaDesigner from 'src/components/widgets/areaDesigner/areaDesigner';
import Loader from 'src/components/widgets/loader';
import { T, stringFormat } from 'src/shared/helpers';
import styled from 'styled-components';
import { Area } from '../../../../widgets/areaDesigner/areaDesigner.model';
import { Dialog, DialogWindow, MessageDialog, QuestionDialog, useDialogManager } from '../../../../widgets/dialog';
import { DisabledModelSide, useProductConfigurationState } from '../../state';
import { ModelColor } from '../../state/model/modelColor';
import { ModelOptions } from '../../state/model/modelOptions';
import { ModelSide } from '../../state/model/modelSide';
import { printAreaEditorDialogMutation } from './__generated__/printAreaEditorDialogMutation.graphql';
import { printAreaEditorDialogQuery } from './__generated__/printAreaEditorDialogQuery.graphql';

const LoaderWrapper = styled.div`
    position: relative;
    top: 34%;
    text-align: -webkit-center;
`;

const CustomDialogWindow = styled(DialogWindow)`
    flex: 1;
    align-self: stretch;
    justify-self: stretch;
    margin: 0 80px;

    > svg:first-child {
        z-index: 2003;
    }
`;

interface PrintAreaEditorDialogProps {
    modelColor: ModelColor;
    modelSide: ModelSide;
    modelOptions: ModelOptions | null;
    disabledModelSides: DisabledModelSide[];
    onSave: (modelSide: ModelSide, modelOptions: ModelOptions | null, isDirty: boolean, areasWereDeleted: boolean) => void;
    onClose: () => void;
}

interface PrintAreaEditorDialogContentProps extends PrintAreaEditorDialogProps {
    onHasChanges: () => void;
}

const PrintAreaEditorDialogContent: FC<PrintAreaEditorDialogContentProps> = (props) => {
    const [modelSide, setModelSide] = useState(props.modelSide);
    const modelSideRef = useRef(props.modelSide);
    const { state } = useProductConfigurationState();
    const { showDialog, closeDialog } = useDialogManager();

    const query = useLazyLoadQuery<printAreaEditorDialogQuery>(
        graphql`
            query printAreaEditorDialogQuery($productIDs: [String!]) {
                printAreaTemplates {
                    templateID
                    name
                    width
                    height
                }
                productIDsWithTemplates(productIDs: $productIDs) {
                    productID
                    templateCount
                }
            }
        `,
        { productIDs: state.product.id ? [state.product.id.toString()] : [] }
    );

    const productHasTemplates = (query.productIDsWithTemplates?.length ?? 0) > 0 && query.productIDsWithTemplates!.some((x) => x.templateCount > 0);

    const [commiCreateTemplate, isSavingTemplate] = useMutation<printAreaEditorDialogMutation>(graphql`
        mutation printAreaEditorDialogMutation($input: PrintAreaTemplateCreateInput!) {
            createPrintAreaTemplate(input: $input) {
                printAreaTemplate {
                    templateID
                    name
                    width
                    height
                }
            }
        }
    `);

    const findTemplateForArea = (area: Area) => {
        const areaWidthCm = area.boundsWidth / modelSide.ppcm;
        const areaHeightCm = area.boundsHeight / modelSide.ppcm;
        return query.printAreaTemplates.find((x) => Math.abs(x!.width - areaWidthCm) < 0.01 && Math.abs(x!.height - areaHeightCm) < 0.01);
    };

    const performSave = (modelOptions: ModelOptions | null, isDirty: boolean, areasWereDeleted: boolean) => {
        const sideToSave = modelSideRef.current;
        sideToSave.areas = sideToSave.areas.filter((x) => x.boundsWidth !== 0 && x.boundsHeight !== 0);

        for (const area of sideToSave.areas) {
            const template = findTemplateForArea(area);
            if (!template) {
                const areaWidthCm = area.boundsWidth / sideToSave.ppcm;
                const areaHeightCm = area.boundsHeight / sideToSave.ppcm;

                commiCreateTemplate({
                    onCompleted: (response) => null,
                    onError: (response) => null,
                    updater: (store) => store.invalidateStore(),
                    variables: {
                        input: {
                            name: area.name != '' ? area.name : T._('Custom size', 'Admin'),
                            width: areaWidthCm,
                            height: areaHeightCm
                        }
                    }
                });
            }
        }

        props.onSave(sideToSave, modelOptions, isDirty, areasWereDeleted);
    };

    const handleSaveClick = (modelOptions: ModelOptions | null, isDirty: boolean, areasWereDeleted: boolean, deletedAreasCount: number) => {
        const sideToSave = modelSideRef.current;
        sideToSave.areas = sideToSave.areas.filter((x) => x.boundsWidth !== 0 && x.boundsHeight !== 0);

        if (sideToSave.areas.length === 0) {
            showDialog('no-areas', <MessageDialog message={T._('Add at last one print area.', 'Admin')} />);
            return;
        }

        if (areasWereDeleted && deletedAreasCount > 0 && state.product.id && productHasTemplates) {
            showDialog(
                'areas-deleted-warning',
                <Dialog
                    buttons={[
                        {
                            label: T._('No', 'Admin'),
                            outline: true,
                            onClick: () => {
                                closeDialog('areas-deleted-warning');
                                props.onClose();
                            }
                        },
                        {
                            label: T._('Yes', 'Admin'),
                            onClick: () => {
                                closeDialog('areas-deleted-warning');
                                performSave(modelOptions, isDirty, areasWereDeleted);
                            }
                        }
                    ]}
                >
                    <p>
                        {stringFormat(T._('We have detected the deletion of {0} print area(s).', 'Admin'), deletedAreasCount.toString())}
                        <br />
                        {T._('This action may affect any pre-designed templates associated with this product.', 'Admin')}
                    </p>
                    <p>
                        {T._(
                            'Items linked to the deleted print area(s) will not be removed. Instead, they will remain in their original position, which may cause them to become partially or completely invisible.',
                            'Admin'
                        )}
                    </p>
                    <p>
                        {T._(
                            'If you proceed with saving, all affected templates will need to be reviewed and updated manually. For more information, please see the following',
                            'Admin'
                        )}{' '}
                        <a href='https://zakeke.zendesk.com/hc/en-us/articles/27271591385756-Consequences-of-print-area-removal-on-products-with-configured-pre-designed-templates' target='_blank' rel='noopener noreferrer'>
                            {T._('guide', 'Admin')}
                        </a>
                        .
                    </p>
                </Dialog>
            );
            return;
        }

        performSave(modelOptions, isDirty, areasWereDeleted);
    };

    return (
        <AreaDesigner
            css={`height: 100%; !important; width: 100% !important;`}
            modelSide={modelSide}
            modelColor={props.modelColor}
            modelOptions={props.modelOptions}
            disabledModelSides={props.disabledModelSides}
            onModelSideChanged={(modelSide) => {
                modelSideRef.current = modelSide;
                setModelSide(modelSide);
                props.onHasChanges();
            }}
            onDirtyChange={(isDirty) => {
                if (isDirty) props.onHasChanges();
            }}
            onSaveClick={handleSaveClick}
        />
    );
};

export const PrintAreaEditorDialog: FC<PrintAreaEditorDialogProps> = (props) => {
    const { showDialog, closeDialog } = useDialogManager();
    const { state } = useProductConfigurationState();
    const hasChangesRef = useRef(false);

    const side = useMemo(
        () => (props.modelSide ? state.sides.find((x) => x.guid == props.modelSide.originalSideGuid) : null),
        [props.modelSide?.originalSideGuid]
    );

    const handleHasChanges = useCallback(() => {
        hasChangesRef.current = true;
    }, []);

    const handleOnClose = () => {
        if (hasChangesRef.current) {
            showDialog(
                'exit',
                <QuestionDialog
                    message={T._('Exit without saving?', 'Admin')}
                    onYesClick={() => {
                        closeDialog('exit');
                        props.onClose();
                    }}
                />
            );
        } else {
            props.onClose();
        }
    };

    return (
        <Dialog title={props.modelColor.name + ' | ' + side!.name} onClose={handleOnClose} windowDecorator={CustomDialogWindow} buttons={[]}>
            <Suspense
                fallback={
                    <LoaderWrapper>
                        <Loader />
                    </LoaderWrapper>
                }
            >
                <PrintAreaEditorDialogContent {...props} onHasChanges={handleHasChanges} />
            </Suspense>
        </Dialog>
    );
};
